import React, { useState, useEffect, useRef, useCallback } from 'react';
import api, { errorMessage } from '../api';
import { useAuth } from '../context/AuthContext';
import Settings from './Settings';
import PostCard from './PostCard';
import usePosts from '../lib/usePosts';
import ProfileMeets from './ProfileMeets';
import { SettingsIcon, BackIcon, CarIcon } from './icons';

const VEHICLE_TYPES = ['', 'EPA', 'A-traktor', 'Moppe', 'Annat'];

export default function Profile({ userId, onBack, onLocationGranted, onViewProfile }) {
  const { user, setUser } = useAuth();
  const isOwnProfile = !userId || userId === user.id;
  const fileRef = useRef(null);

  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [bio, setBio] = useState('');
  const [vehicleType, setVehicleType] = useState('');
  const [vehicleBrand, setVehicleBrand] = useState('');
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [section, setSection] = useState('posts');
  const [friendBusy, setFriendBusy] = useState(false);

  // Inläggen hämtas för den profil som visas, inte bara den egna.
  const shownUserId = userId || user.id;
  const { posts, loading: loadingPosts, hasMore, loadMore, remove } = usePosts(`/posts/user/${shownUserId}`);

  const fillForm = useCallback((data) => {
    setBio(data.bio || '');
    setVehicleType(data.vehicle_type || '');
    setVehicleBrand(data.vehicle_brand || '');
  }, []);

  useEffect(() => {
    if (isOwnProfile) {
      setProfile(user);
      fillForm(user);
      return undefined;
    }

    let cancelled = false;
    setProfile(null);
    api.get(`/user/${userId}`)
      .then(res => { if (!cancelled) setProfile(res.data); })
      .catch(err => { if (!cancelled) setStatus({ type: 'error', text: errorMessage(err, 'Profilen kunde inte hämtas.') }); });
    return () => { cancelled = true; };
  }, [userId, isOwnProfile, user, fillForm]);

  /**
   * Skickar en vanforfragan till personen vars profil visas.
   *
   * Finns redan en forfragan at andra hallet accepterar servern den i
   * stallet for att skapa en dubblett, sa samma knapp funkar for bada.
   */
  const addFriend = async () => {
    setFriendBusy(true);
    setStatus(null);
    try {
      const res = await api.post('/friends/request', { username: profile.username });
      setStatus({ type: 'success', text: res.data.message });
      setProfile(prev => ({
        ...prev,
        relation: res.data.status === 'accepted' ? 'friends' : 'pending_out',
        friend_count: res.data.status === 'accepted'
          ? (prev.friend_count || 0) + 1
          : prev.friend_count,
      }));
    } catch (err) {
      setStatus({ type: 'error', text: errorMessage(err) });
    } finally {
      setFriendBusy(false);
    }
  };

  /** Knappen ratter sig efter hur ni star till varandra. */
  const friendAction = () => {
    switch (profile.relation) {
      case 'friends':
        return <p className="profile__relation">✓ Ni är vänner</p>;
      case 'pending_out':
        return <p className="profile__relation">Vänförfrågan skickad</p>;
      case 'pending_in':
        return (
          <button className="btn btn--primary btn--block" onClick={addFriend} disabled={friendBusy}>
            {friendBusy ? 'Ett ögonblick...' : `Acceptera ${profile.username}s vänförfrågan`}
          </button>
        );
      case 'self':
        return null;
      default:
        return (
          <button className="btn btn--primary btn--block" onClick={addFriend} disabled={friendBusy}>
            {friendBusy ? 'Skickar...' : '+ Lägg till som vän'}
          </button>
        );
    }
  };

  const save = async () => {
    setStatus(null);
    try {
      const res = await api.put('/user/profile', {
        bio,
        vehicle_type: vehicleType,
        vehicle_brand: vehicleBrand,
      });
      setUser(res.data);
      setProfile(res.data);
      setEditing(false);
      setStatus({ type: 'success', text: 'Sparat!' });
      setTimeout(() => setStatus(null), 2000);
    } catch (err) {
      setStatus({ type: 'error', text: errorMessage(err, 'Profilen kunde inte sparas.') });
    }
  };

  const uploadPicture = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setStatus(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('picture', file);
      const res = await api.post('/user/profile-picture', formData);
      setUser(res.data);
      setProfile(res.data);
    } catch (err) {
      setStatus({ type: 'error', text: errorMessage(err, 'Bilden kunde inte laddas upp.') });
    } finally {
      setUploading(false);
      // Nollställ, annars går det inte att välja samma fil igen.
      event.target.value = '';
    }
  };

  if (showSettings) {
    return (
      <div>
        <button className="btn btn--ghost" onClick={() => setShowSettings(false)}>
          <BackIcon size={17} /> Tillbaka
        </button>
        <Settings onLocationGranted={onLocationGranted} />
      </div>
    );
  }

  if (!profile) {
    return (
      <div>
        {status && <p className={`notice notice--${status.type}`}>{status.text}</p>}
        {!status && <p className="muted">Laddar profil...</p>}
      </div>
    );
  }

  const memberSince = profile.created_at
    ? new Date(profile.created_at).toLocaleDateString('sv-SE')
    : null;

  return (
    <div>
      <div className="profile-top">
        {onBack ? (
          <button className="btn btn--ghost" onClick={onBack}>
            <BackIcon size={17} /> Tillbaka
          </button>
        ) : <span />}

        {isOwnProfile && (
          <button
            className="profile-top__settings"
            onClick={() => setShowSettings(true)}
            aria-label="Inställningar"
          >
            <SettingsIcon size={21} />
          </button>
        )}
      </div>

      <div className="center">
        {profile.profile_picture ? (
          <img className="avatar" src={profile.profile_picture} alt={`Profilbild för ${profile.username}`} />
        ) : (
          <div className="avatar avatar--placeholder" aria-hidden="true">
            {profile.username.charAt(0).toUpperCase()}
          </div>
        )}

        {isOwnProfile && (
          <>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              ref={fileRef}
              onChange={uploadPicture}
              hidden
            />
            <button className="btn btn--ghost" onClick={() => fileRef.current.click()} disabled={uploading}>
              {uploading ? 'Laddar upp...' : 'Byt profilbild'}
            </button>
          </>
        )}
      </div>

      <h3 className="center">{profile.username}</h3>

      {status && <p className={`notice notice--${status.type}`}>{status.text}</p>}

      {!editing ? (
        <div>
          {profile.bio && <p className="profile__field">{profile.bio}</p>}
          {/* Fordonet ar halva poangen med profilen och far ett eget kort. */}
          {(profile.vehicle_type || profile.vehicle_brand) && (
            <div className="vehicle-card">
              <span className="vehicle-card__icon"><CarIcon size={26} /></span>
              <span className="vehicle-card__text">
                <strong>{profile.vehicle_brand || profile.vehicle_type}</strong>
                {profile.vehicle_brand && profile.vehicle_type && (
                  <span>{profile.vehicle_type}</span>
                )}
              </span>
            </div>
          )}
          {memberSince && <p className="small muted">Medlem sedan {memberSince}</p>}
          {profile.friend_count !== undefined && (
            <p className="small muted">{profile.friend_count} vänner</p>
          )}
          {!isOwnProfile && (
            <div style={{ marginTop: 'var(--space-md)' }}>{friendAction()}</div>
          )}

          {isOwnProfile && (
            <button className="btn btn--block" onClick={() => setEditing(true)} style={{ marginTop: 'var(--space-md)' }}>
              Redigera profil
            </button>
          )}
        </div>
      ) : (
        <div>
          <div className="field">
            <label className="field__label" htmlFor="profile-bio">Bio</label>
            <textarea
              id="profile-bio"
              value={bio}
              onChange={e => setBio(e.target.value)}
              maxLength={500}
              rows={3}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="profile-vehicle-type">Fordonstyp</label>
            <select id="profile-vehicle-type" value={vehicleType} onChange={e => setVehicleType(e.target.value)}>
              {VEHICLE_TYPES.map(type => (
                <option key={type} value={type}>{type || '-- Välj --'}</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="profile-vehicle-brand">Fordon (märke/modell)</label>
            <input
              id="profile-vehicle-brand"
              value={vehicleBrand}
              onChange={e => setVehicleBrand(e.target.value)}
              maxLength={100}
              placeholder="t.ex. Volvo 740"
            />
          </div>

          <div className="row">
            <button className="btn btn--primary grow" onClick={save}>Spara</button>
            <button className="btn btn--neutral grow" onClick={() => { setEditing(false); fillForm(profile); }}>
              Avbryt
            </button>
          </div>
        </div>
      )}

      <div className="tabs" style={{ marginTop: 'var(--space-xl)' }}>
        <button
          className={section === 'posts' ? 'is-active' : ''}
          onClick={() => setSection('posts')}
        >
          Inlägg
        </button>
        <button
          className={section === 'meets' ? 'is-active' : ''}
          onClick={() => setSection('meets')}
        >
          Träffar
        </button>
      </div>

      {section === 'posts' && (
        <>
          {loadingPosts && <p className="empty">Hämtar inlägg…</p>}
          {!loadingPosts && posts.length === 0 && (
            <p className="empty">
              {isOwnProfile ? 'Du har inte lagt upp något än.' : 'Inga inlägg att visa.'}
            </p>
          )}

          {posts.map(post => (
            <PostCard key={post.id} post={post} onDeleted={remove} onViewProfile={onViewProfile} />
          ))}

          {hasMore && (
            <button className="btn btn--outline btn--block" onClick={loadMore}>
              Visa äldre inlägg
            </button>
          )}
        </>
      )}

      {/* Traffarna ar personliga; en annans profil visar bara inlagg. */}
      {section === 'meets' && (
        isOwnProfile
          ? <ProfileMeets />
          : <p className="empty">Träffar visas bara på din egen profil.</p>
      )}
    </div>
  );
}
